/**
 * Integer-only greedy player used by the solver bot, the shuffle solvability
 * check and duel bots. Deterministic for a given RNG state.
 */
import { rngChance, rngInt, type RngState } from './rng.js';
import { PIECE_BAG, PIECE_BOARD, PIECE_GONE, doTap, glueEscape, gluePartner, lineShown, listTappable, trayEscape, type Round } from './state.js';
import { Kind, Status } from './types.js';

export interface PolicyParams {
  /** per-mille chance to not take the best move (models human error) */
  eps: number;
  /** candidates considered when erring */
  topK: number;
}

export const SKILLED: PolicyParams = { eps: 60, topK: 3 };
export const CASUAL: PolicyParams = { eps: 220, topK: 4 };

/** Bonus for the pieces a tap on board tile i uncovers. */
function uncoverScore(r: Round, i: number, needed: Uint8Array): number {
  const L = r.L;
  let s = 0;
  for (let p = L.underStart[i]!; p < L.underStart[i + 1]!; p++) {
    const u = L.underList[p]!;
    if (r.cover[u] === 1 && r.piece[u] === PIECE_BOARD) s += needed[r.item[u]!] ? 250 : 120;
  }
  return s;
}

/**
 * lockKey: extra score for putting one more key item into the bag while the locks are shut (0 for
 * any other item, and on every level without locks). The key is worth most when its insert pops
 * the triple (`inBag` = 2) and when the padlocks hold ticket items: each locked tile on the board
 * holding an item the ticket still needs adds to it.
 */
export function keyBonus(r: Round, k: number, inBag: number, needed: Uint8Array): number {
  if (!r.lockShut || k !== r.L.lockKey) return 0;
  let held = 0;
  for (const i of r.L.lockedTiles) if (r.piece[i] === PIECE_BOARD && needed[r.item[i]!]) held++;
  const per = inBag === 2 ? 1200 : inBag === 1 ? 300 : 100;
  return per * (1 + held);
}

/** Bag pressure after a tap that leaves `after` items when the bag was not emptied by a pop. */
function pressure(r: Round, after: number): number {
  if (after >= r.cap) return -50000;
  if (after >= r.cap - 1) return -6000;
  if (after >= r.cap - 2) return -2000;
  return 0;
}

/**
 * Score for tapping glued tile i together with partner p (both free). The two inserts are scored
 * as the single-tile terms of each item, then the bag pressure of the pair's net growth: the bag
 * after both inserts and any pops, as doTap plays them (one tap takes 2 slots).
 */
function scorePair(r: Round, i: number, p: number, bagCounts: Int16Array, needed: Uint8Array, visible: Int16Array): number {
  const a = r.item[i]!;
  const b = r.item[p]!;
  let s = uncoverScore(r, i, needed) + uncoverScore(r, p, needed);
  let after = r.bagLen;
  if (a === b) {
    const c = bagCounts[a]!;
    if (c >= 1) s += 10000; // the pair completes a triple (c = 1: its second insert; c = 2: its first)
    else s += 3000;
    after += c >= 1 ? -1 : 2;
    if (needed[a]) s += 1000;
  } else {
    s += itemScore(a, bagCounts, needed, visible) + itemScore(b, bagCounts, needed, visible);
    after += (bagCounts[a] === 2 ? -2 : 1) + (bagCounts[b] === 2 ? -2 : 1);
  }
  if (r.lockShut) s += keyBonus(r, a, a === b ? Math.min(2, bagCounts[a]! + 1) : bagCounts[a]!, needed) + (a === b ? 0 : keyBonus(r, b, bagCounts[b]!, needed));
  if (after > r.bagLen) s += pressure(r, after);
  return s;
}

/** The single-insert terms of item k (as scoreTap scores one tile of it). */
function itemScore(k: number, bagCounts: Int16Array, needed: Uint8Array, visible: Int16Array): number {
  const c = bagCounts[k]!;
  let s = c === 2 ? 10000 : c === 1 ? 3000 : 0;
  if (needed[k]) s += 1000;
  if (c + visible[k]! >= 3) s += 800;
  return s;
}

/**
 * Score for tapping tray piece i (its first piece): a sure pop that takes no slot. The pop's
 * 10000, the ticket's 1000 when its item is needed, the key bonus of a popping key triple, and the
 * pieces it uncovers valued as under a tile; no bag-pressure term (the bag is untouched) and no
 * visible-set term (the tray is a triple on its own).
 */
function scoreTray(r: Round, i: number, needed: Uint8Array): number {
  const L = r.L;
  const k = r.item[i]!;
  let s = 10000;
  if (needed[k]) s += 1000;
  if (r.lockShut) s += keyBonus(r, k, 2, needed);
  // all three tray pieces cover what lies under the tray: a piece is freed when they are its only cover
  for (let p = L.underStart[i]!; p < L.underStart[i + 1]!; p++) {
    const u = L.underList[p]!;
    if (r.cover[u] === 3 && r.piece[u] === PIECE_BOARD) s += needed[r.item[u]!] ? 250 : 120;
  }
  return s;
}

/** Shelf: the first piece still on the board from index `j`, stepping by `d` (±1) inside [a, b); -1 when none. */
function shelfStep(r: Round, j: number, d: number, a: number, b: number): number {
  for (; j >= a && j < b; j += d) if (r.piece[j] === PIECE_BOARD) return j;
  return -1;
}

/**
 * Shelf: the reveal term of taking end i, mirroring the uncover terms of a tile. The item that
 * becomes an end in its place (i's neighbour still on the shelf) scores 250 when its item is
 * needed, 120 when not; nothing when i is the shelf's last item, or when that neighbour is the
 * other end already (only the two ends were left). The whole row is visible, so the player knows
 * that item: there is no information-set issue.
 */
function shelfRevealScore(r: Round, i: number, needed: Uint8Array): number {
  const L = r.L;
  const s = L.shelfOf[i]!;
  const a = L.shelfStart[s]!;
  const b = L.shelfStart[s + 1]!;
  const prev = shelfStep(r, i - 1, -1, a, b);
  const next = shelfStep(r, i + 1, 1, a, b);
  // i is the left end (nothing before it): its right neighbour is exposed, and vice versa
  const d = prev < 0 ? 1 : -1;
  const exposed = prev < 0 ? next : next < 0 ? prev : -1;
  if (exposed < 0 || shelfStep(r, exposed + d, d, a, b) < 0) return 0;
  return needed[r.item[exposed]!] ? 250 : 120;
}

/** Score for tapping piece i. Higher is better. */
export function scoreTap(r: Round, i: number, bagCounts: Int16Array, needed: Uint8Array, visible: Int16Array): number {
  const L = r.L;
  if (L.nGlue && L.kind[i] === Kind.Tile) {
    const p = gluePartner(r, i);
    if (p >= 0) return scorePair(r, i, p, bagCounts, needed, visible);
  }
  if (L.nTrays && L.kind[i] === Kind.Tray) return scoreTray(r, i, needed);
  const k = r.item[i]!;
  const inBag = bagCounts[k]!;
  let s = 0;
  if (r.ice[i]! > 0) s -= 150;
  if (inBag === 2) s += 10000;
  else if (inBag === 1) s += 3000;
  if (needed[k]) s += 1000;
  if (inBag + visible[k]! >= 3) s += 800;
  if (r.lockShut) s += keyBonus(r, k, inBag, needed);
  if (L.kind[i] === Kind.Tile) {
    for (let p = L.underStart[i]!; p < L.underStart[i + 1]!; p++) {
      const u = L.underList[p]!;
      if (r.cover[u] === 1 && r.piece[u] === PIECE_BOARD) s += needed[r.item[u]!] ? 250 : 120;
    }
  } else if (L.kind[i] === Kind.Dispenser) {
    // the player sees the machine's top item only: the next one it reveals scores as an unknown
    // piece (120), what the machine stands on (once it empties) as it would under a tile
    for (let p = L.underStart[i]!; p < L.underStart[i + 1]!; p++) {
      const u = L.underList[p]!;
      if (r.cover[u] === 1 && r.piece[u] === PIECE_BOARD) s += L.dispOf[u] === L.dispOf[i] || !needed[r.item[u]!] ? 120 : 250;
    }
  } else if (L.kind[i] === Kind.Shelf) s += shelfRevealScore(r, i, needed);
  if (inBag < 2) {
    const after = r.bagLen + 1;
    if (after >= r.cap) s -= 50000;
    else if (after >= r.cap - 1) s -= 6000;
    else if (after >= r.cap - 2) s -= 2000;
  }
  return s;
}

export class Greedy {
  private cand: Int32Array;
  private scores: Int32Array;
  private bagCounts: Int16Array;
  private needed: Uint8Array;
  private visible: Int16Array;

  constructor(nPieces: number, nItems: number) {
    this.cand = new Int32Array(nPieces);
    this.scores = new Int32Array(nPieces);
    this.bagCounts = new Int16Array(nItems);
    this.needed = new Uint8Array(nItems);
    this.visible = new Int16Array(nItems);
  }

  /** Choose next tap or -1 if nothing is tappable. */
  choose(r: Round, rng: RngState, p: PolicyParams): number {
    const n = listTappable(r, this.cand);
    if (n === 0) return -1;
    this.bagCounts.fill(0);
    this.needed.fill(0);
    this.visible.fill(0);
    for (let s = 0; s < r.bagLen; s++) this.bagCounts[r.item[r.bag[s]!]!]!++;
    // clearGoal: every item must pop, so every item is needed (the ticket's terms no longer single any out)
    if (r.L.clear) this.needed.fill(1);
    // hiddenLine: a face-down line's item is not known, so it is valued as clutter until revealed
    else for (let l = 0; l < r.L.nLines; l++) if (r.remaining[l]! > 0 && lineShown(r, l)) this.needed[r.L.lineItem[l]!] = 1;
    // collectGoal: golden markers are visible, on the board and in the bag. Their current item
    // labels are honest player information (shuffle moves labels but not the markers).
    if (r.L.collect) for (const i of r.L.goldenTiles) if (r.piece[i] !== PIECE_GONE) this.needed[r.item[i]!] = 1;
    // a tray's items pop on their own and never pair with the bag's: it adds nothing to the visible count
    for (let c = 0; c < n; c++) if (!r.L.nTrays || r.L.kind[this.cand[c]!] !== Kind.Tray) this.visible[r.item[this.cand[c]!]!]!++;
    // a glued pair is one candidate (its lower tile): its partner's item is on show as well
    if (r.L.nGlue)
      for (let c = 0; c < n; c++) {
        const q = r.L.kind[this.cand[c]!] === Kind.Tile ? gluePartner(r, this.cand[c]!) : -1;
        if (q >= 0) this.visible[r.item[q]!]!++;
      }
    for (let c = 0; c < n; c++) {
      const i = this.cand[c]!;
      this.scores[c] = scoreTap(r, i, this.bagCounts, this.needed, this.visible) * 4096 - c; // stable tiebreak
    }
    // partial selection sort for the top K
    const k = Math.min(p.topK, n);
    for (let a = 0; a < k; a++) {
      let best = a;
      for (let b = a + 1; b < n; b++) if (this.scores[b]! > this.scores[best]!) best = b;
      if (best !== a) {
        const ts = this.scores[a]!;
        this.scores[a] = this.scores[best]!;
        this.scores[best] = ts;
        const tc = this.cand[a]!;
        this.cand[a] = this.cand[best]!;
        this.cand[best] = tc;
      }
    }
    if (k > 1 && rngChance(rng, p.eps)) return this.cand[1 + rngInt(rng, k - 1)]!;
    return this.cand[0]!;
  }
}

/** Play the round to the end with taps only (mutates r). */
export function playGreedy(r: Round, rng: RngState, p: PolicyParams, g = new Greedy(r.L.n, r.L.items.length)): Round {
  let t = r.lastT;
  while (r.status === Status.Playing) {
    // one slot left and no pair in the bag: every tap fills the bag without a triple (unless a
    // glued pair of one item pops on its own second insert, or an uncovered tray pops without a slot)
    if (r.bagLen >= r.cap - 1 && !hasPair(r) && !glueEscape(r) && !trayEscape(r)) break;
    const i = g.choose(r, rng, p);
    if (i < 0) break;
    t += 250;
    doTap(r, i, t, null);
  }
  return r;
}

function hasPair(r: Round): boolean {
  for (let a = 0; a + 1 < r.bagLen; a++) if (r.item[r.bag[a]!] === r.item[r.bag[a + 1]!]) return true;
  return false;
}

export function bagCountsOf(r: Round, nItems: number): Int16Array {
  const out = new Int16Array(nItems);
  for (let s = 0; s < r.bagLen; s++) out[r.item[r.bag[s]!]!]!++;
  return out;
}

export { PIECE_BAG };
